import { Component, OnInit, ViewChild } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { MatStepper } from '@angular/material/stepper'
import { getCompany, getEntities, getSopSettings } from '../../store/selector/onboarding.selector'
import { filter, Subject, take, takeUntil } from 'rxjs'
import { Store } from '@ngrx/store'
import { OnboardingService } from '../../services/onboarding-wizard/onboarding.service'
import { emailValidatorForNonMandatory, VALIDATORS } from '../../validators/validators.validator'
import { Actions, ofType } from '@ngrx/effects'
import { onboardingGoToNextStep } from 'src/app/store/action/onboarding.action'

@Component({
  selector: 'app-onboarding-wizard',
  templateUrl: './onboarding-wizard.component.html'
})
export class OnboardingWizardComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper

  private destroy$ = new Subject<void>()

  isLinear = true
  isEditMode = false
  mode: 'new' | 'edit' = 'new'
  companyId: string | null = null

  companyForm!: FormGroup
  entityForm!: FormGroup
  sopForm!: FormGroup
  empForm!: FormGroup
  wfForm!: FormGroup

  entities!: FormArray

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private onboardingService: OnboardingService,
    private actions$: Actions
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.mode = params['mode'] || 'new'
      this.companyId = params['companyId'] || null
      this.isEditMode = this.mode === 'edit'
    })

    this.initializeForms()

    // Keep latest company id from store (after step 1 save)
    this.store
      .select(getCompany)
      .pipe(filter(c => !!c))
      .subscribe(c => {
        this.companyId = c!.id
      })

    this.actions$.pipe(ofType(onboardingGoToNextStep), takeUntil(this.destroy$)).subscribe(({ props }) => {
      if (props?.type === 'FORM_11_PDF_CONFIG' || props?.type === 'EMPLOYEE_ADMINS') {
        this.stepper.next()
      }
    })
  }

  initializeForms() {
    this.companyForm = this.fb.group({
      companyName: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(90), VALIDATORS.ALPHA_NUMERIC_WITH_SPACES]
      ],

      companyDisplayId: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(8), VALIDATORS.ALPHA_NUMERIC_WITH_SPACES]
      ],

      doorNo: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(8), VALIDATORS.ALPHA_NUMERIC_WITH_SPACES]
      ],

      street: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(90), VALIDATORS.ALPHA_NUMERIC_WITH_SPACES]
      ],

      area: ['', [Validators.minLength(3), Validators.maxLength(90), VALIDATORS.ALPHA_NUMERIC_WITH_SPACES]],

      city: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(20), VALIDATORS.ONLY_APLHABETS_WITH_SPACES]
      ],

      state: [
        '',
        [Validators.required, Validators.minLength(2), Validators.maxLength(15), VALIDATORS.ONLY_APLHABETS_WITH_SPACES]
      ],

      pincode: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(8), VALIDATORS.ONLY_NUMBER]],

      country: ['', Validators.required],

      logoFile: [null]
    })

    // STEP 2 — Entities (FormArray)
    this.entities = this.fb.array([this.createEntityGroup()])

    this.entityForm = this.fb.group({
      entities: this.entities
    })

    this.sopForm = this.fb.group({
      modules: this.fb.group({
        ACTIVITY_TRACKER: [true],
        GOVERNMENT_BUSINESS_TASK: [true],
        CALENDAR: [true],
        CIRCULARS_AND_ANNOUNCEMENTS: [true],
        KNOWLEDGE_BANK: [true],
        DOCUMENT_REPOSITORY: [true],
        QUERIES: [true],
        DASHBOARD: [true],
        OFFLINE_TRACKER: [true]
      }),

      categories: this.fb.group({
        EPF: [true],
        ESI: [true],
        PT: [true],
        LABOUR: [true],
        OTHER_LLC: [true]
      }),

      notificationPreferences: this.fb.group({
        EMAIL: [{ value: true, disabled: true }],
        SMS: [false],
        WHATSAPP: [false]
      }),

      rmsAdminUserIds: [[]]
    })

    // STEP 4 — Employees
    this.empForm = this.fb.group({
      employees: ['']
    })

    // STEP 5 — Workflow
    this.wfForm = this.fb.group({
      workflow: ['']
    })
  }

  createEntityGroup(): FormGroup {
    return this.fb.group({
      entityName: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(90), VALIDATORS.ALPHA_NUMERIC_WITH_SPACES]
      ],
      entityDisplayId: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(8),
          Validators.pattern(/^[a-zA-Z0-9\s-]*$/) // includes hyphen
        ]
      ],
      contactPersonName: [
        '',
        [Validators.minLength(3), Validators.maxLength(50), VALIDATORS.ALPHA_NUMERIC_WITH_SPACES]
      ],
      contactPersonMobile: [
        '',
        [Validators.minLength(13), Validators.maxLength(13), Validators.pattern(/^\d{2}\s\d{10}$/)]
      ],
      contactPersonEmail: ['', [Validators.minLength(10), Validators.maxLength(40), emailValidatorForNonMandatory()]],
      locations: this.fb.array([this.createLocationGroup()])
    })
  }

  createLocationGroup(): FormGroup {
    return this.fb.group({
      locationCode: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(20),
          Validators.pattern(/^[a-zA-Z0-9-]*$/) // code like SPC-CBE-01
        ]
      ],
      address: this.fb.group({
        doorNo: [''],
        street: [''],
        area: [''],
        city: [''],
        pincode: [''],
        state: [''],
        country: ['']
      })
    })
  }

  // expose entities as FormArray if needed in TS
  get entitiesArray(): FormArray {
    return this.entityForm.get('entities') as FormArray
  }

  addEntity() {
    this.entitiesArray.push(this.createEntityGroup())
  }

  removeEntity(index: number) {
    this.entitiesArray.removeAt(index)
  }

  // Navigation
  goBack(stepper: MatStepper) {
    stepper.previous()
  }

  saveAndNext(stepper: MatStepper, stepIndex: number) {
    console.log(`Saving Step ${stepIndex + 1}`)
    // later: call backend here before moving
    stepper.next()
  }

  goToNextStep(stepper: MatStepper) {
    stepper.next()
  }

  discardStep() {
    this.companyForm.reset()
  }

  onCompanySave(stepper: MatStepper) {
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched()
      return
    }

    const payload = this.onboardingService.mapCompanyFormToPayload(this.companyForm.value)

    this.onboardingService.dispatchSaveCompanyAction(payload, null)

    this.store
      .select(getCompany)
      .pipe(
        filter(c => !!c),
        take(1)
      )
      .subscribe(() => stepper.next())
  }

  onEntitySave(stepper: MatStepper) {
    if (this.entityForm.invalid) {
      this.entities.markAllAsTouched()
      return
    }

    const payload = this.onboardingService.mapEntitiesFormToPayload(this.entityForm.value, this.companyId!)

    this.onboardingService.dispatchSaveEntitiesAction(payload)

    this.store
      .select(getEntities)
      .pipe(
        filter(res => res && res.length > 0),
        take(1)
      )
      .subscribe(() => {
        stepper.next()
      })
  }

  onSopSave(stepper: MatStepper) {
    if (this.sopForm.invalid) {
      this.sopForm.markAllAsTouched()
      return
    }

    const payload = this.onboardingService.mapSopFormToPayload(this.sopForm.value, this.companyId!)

    this.onboardingService.dispatchSaveSopSettingsAction(payload)

    this.store
      .select(getSopSettings)
      .pipe(
        filter(s => !!s),
        take(1)
      )
      .subscribe(() => {
        stepper.next()
      })
  }

  onDiscard() {
    this.companyForm.reset()
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
